import styled from "@emotion/styled";
import {
  type ConfirmModalDefinition,
  type Customer,
  type OpportunitySearch,
  ProjectFilterDefault,
  createAndUpdateTask,
  initialOpportunityOptions,
  mapSearchResultToOpportunityArray,
  updateProject,
  useOpportunitiesSearch,
} from "@repo/api";
import { ListView, ListViewItem, Loading } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  hasItems,
  isDefined,
  maskEmail,
  maskPhoneNumber,
  trackAnalytics,
} from "@repo/utils";
import { type Locale } from "date-fns";

import { CoWorkerName } from "../../components/CoWorkerName";
import { CustomerName } from "../../components/CustomerName";
import { type Info, InfoBox } from "../../components/InfoBox";
import { Line } from "../../components/Line";
import { localizeDate } from "../../components/LocalizedDate";
import { Name } from "../../components/Name";
import { getOneLineInfo } from "../../components/OneLineInfo";
import { SectionContainer } from "../../components/SectionContainer";
import { useToast } from "../../components/Toast";
import { useSettings } from "../../settings/SettingsProvider";
import { getFormattedAddress } from "../../utils/customerHelpers";
import { translateProjectStatus } from "../../utils/translationHelpers";
import { type NewProjectState } from "./NewProjectReducer";

interface SelectProjectStepProps {
  state: NewProjectState;
  customer?: Customer;
  onSelectProject: (projectId: number) => void;
  pushModal: (modalProps: ConfirmModalDefinition) => void;
  filterCurrentProject?: number;
}

const CustomerInfoContainer = styled.div`
  margin-bottom: 24px;
`;

export const getCustomerInfo = (
  customer: Customer,
  useAddressFormat?: boolean,
  customerMode?: boolean
): Info[] =>
  [
    customer.customerType === "Business"
      ? {
          label: t("label.contact"),
          value: (
            <Name firstName={customer.firstName} lastName={customer.lastName} />
          ),
        }
      : undefined,
    {
      label: t("label.address"),
      value: getOneLineInfo(
        getFormattedAddress(customer, useAddressFormat, customerMode)
      ),
    },
    {
      label: t("label.phone"),
      value: customerMode
        ? maskPhoneNumber(customer.phoneNumber)
        : customer.phoneNumber,
    },
    {
      label: t("label.email"),
      value: customerMode ? maskEmail(customer.email) : customer.email,
    },
  ].filter(isDefined);

export const getOpportunityInfo = (
  opportunity: OpportunitySearch,
  dateFnsLocale?: Locale
): Info[] => [
  {
    label: t("label.id"),
    value: opportunity.opportunityId,
  },
  {
    label: t("label.status"),
    value: translateProjectStatus(opportunity.status),
  },
  {
    label: t("label.cmp"),
    value: opportunity.buName,
  },
  { label: t("label.department"), value: "-" }, // TODO: need HFB in opportunity search API
  {
    label: t("label.last_updated"),
    value: opportunity.updatedAt
      ? localizeDate(opportunity.updatedAt, dateFnsLocale)
      : "-",
  },
  {
    label: t("label.owner"),
    value: (
      <CoWorkerName email={opportunity.assignedTo} textStyle="Heading.XS" />
    ),
  },
];

export const SelectProjectStep = ({
  customer,
  state,
  onSelectProject,
  pushModal,
  filterCurrentProject,
}: SelectProjectStepProps) => {
  const { dateFnsLocale } = useTranslation();
  const { updateToast } = useToast();
  const { customerMode } = useSettings();
  // Search by customer email to find all projects related to the customer (registered, unregistered)
  const { data, isValidating } = useOpportunitiesSearch(
    { ...ProjectFilterDefault, term: customer?.email },
    { ...initialOpportunityOptions }
  );
  const result = mapSearchResultToOpportunityArray(data, filterCurrentProject);

  return (
    <>
      <CustomerInfoContainer>
        <InfoBox
          info={getCustomerInfo(customer!, true, customerMode)}
          title={<CustomerName customer={customer} />}
          showTitle
        />
      </CustomerInfoContainer>

      <Line />

      {isValidating && (
        <SectionContainer>
          <Loading text={t("prompt.loading")} data-testid="search-loading" />
        </SectionContainer>
      )}
      {!isValidating && hasItems(data) && (
        <SectionContainer title={t("label.existing_projects")}>
          <ListView id="search-result">
            {result?.map((opportunity) => (
              <ListViewItem
                key={opportunity.opportunityId}
                title={opportunity.title}
                description={
                  <InfoBox
                    info={getOpportunityInfo(opportunity, dateFnsLocale)}
                  />
                }
                control="navigational"
                onClick={() => {
                  if (state.associatedDropInAppointment?.id) {
                    pushModal({
                      type: "CONFIRM",
                      onConfirm: () => {
                        trackAnalytics("associate_task");
                        updateProject(
                          {
                            id: opportunity?.opportunityId,
                            dropInAppointmentId:
                              state.associatedDropInAppointment?.id,
                          },
                          updateToast
                        ).then(() => {
                          onSelectProject(opportunity.opportunityId);
                        });
                      },
                      title: t("action.add_to_project"),
                      message: t("prompt.confirm_add_task_to_project"),
                    });
                  } else if (state.associatedTask) {
                    pushModal({
                      type: "CONFIRM",
                      onConfirm: () => {
                        trackAnalytics("associate_task");
                        createAndUpdateTask({
                          ...state.associatedTask,
                          opportunityId: opportunity.opportunityId,
                        }).then(() => {
                          onSelectProject(opportunity.opportunityId);
                        });
                      },
                      title: t("action.add_to_project"),
                      message: t("prompt.confirm_add_task_to_project"),
                    });
                  } else {
                    onSelectProject(opportunity.opportunityId);
                  }
                }}
              />
            ))}
          </ListView>
        </SectionContainer>
      )}
    </>
  );
};
